<script>
  import _lowerCase from 'lodash/lowerCase'
  import _startCase from 'lodash/startCase'
  import { pluralizeMethods } from '@state/helpers'
  import { checkVisibility } from '@utils/modal'

  import {
    ExternalLinkIcon,
    AlertCircleIcon
  } from 'vue-feather-icons'
  export default {
    components: {
      ExternalLinkIcon,
      AlertCircleIcon,
    },
    props: {
      action: {
        type: String,
        required: true,
      },
      title: {
        type: String,
        required: true,
      },
      message: {
        type: String,
        required: true,
      },
      customInfoMessage: {
        type: String,
        default: '',
      },
      documents: {
        type: Array,
        required: true,
      },
      docType: {
        type: String,
        required: true,
      },
      outstandingType: {
        type: String,
        default: 'Documents',
      },
      id: {
        type: String,
        default: 'negative-state-modal',
      },
      okText: {
        type: String,
        default: 'Confirm'
      },
      hideColumn: {
        type: Array,
        default: () => [''],
      },
    },
    data() {
      return {
        error: '',
        note: '',
        baseFields: [
          'document_name',
          'status',
          'actions',
        ],
        docuemntsData: this.documents,
      }
    },
    computed: {
      fields(){
        if (this.hideColumn){
          return this.baseFields.filter(value => !this.hideColumn.includes(value));
        }
        return this.baseFields
      },
      titlesMap() {
        return {
          document_name : `outstanding ${this.outstandingType}`,
          status : 'status',
          actions : 'action'
        }
      },
      MAX_VISIBLE_DOCUMENTS(){
        return 4
      },
      infoMessage(){
        if (this.customInfoMessage){
          return this.customInfoMessage
        }
        if (!this.docType || !this.action){
          return ''
        }
        return `You will need to resolve the above documents before you can ${this.action} this ${this.docType}.`
      },
      secondaryInfoMessageParts() {
        if (this.totalDocCount <= this.MAX_VISIBLE_DOCUMENTS){
          return {
            first : ``,
            second : ``,
            third : ``
          }
        }
        return {
          first : `There are an additional `,
          second : `${ (this.totalDocCount - this.MAX_VISIBLE_DOCUMENTS === 1) ? '' : (this.totalDocCount - this.MAX_VISIBLE_DOCUMENTS)} `,
          third : `${this.pluralize( 'document', this.totalDocCount - this.MAX_VISIBLE_DOCUMENTS )} not displayed at this time, which will also need to be resolved.`
        }
      },
      totalDocCount(){
        if (!this.documents){
          return 0
        }
        return this.documents.length
      },
      slicedDocuments(){
        if (!this.documents){
          return []
        }
        return this.documents.slice(0,4)
      },
      modalElementId(){
        return `${this.id}___FB_modal_outer_`
      }
    },
    methods: {
      checkVisibility,
      _startCase,
      _lowerCase,
      ...pluralizeMethods,
      tryToEdit(item){
        const params = {}
        if (item.project_id){
          params.id = item.project_id
        }
        const routeData = this.$router.resolve(this.resolveRouterObjectForDocument(item, params));
        window.open(routeData.href, '_blank');
      },
      close(){
        this.$customModal.hide(this.id)
        this.$emit('negativeStateModalClosed')
      }
    },
  }
</script>

<template>
  <FabricModal
    :id="id"
    :heading="title"
    size="lg"
    centered
    no-close-on-backdrop
    no-close-on-esc
    header-class="border-bottom-primary pd-x-0-f tw-border-b-2 tw-border-primary"
    @shown="checkVisibility('modal-product-listing___FB_modal_outer_')"
  >
    <template v-slot:modal-header>
      <div class="custom-border-header-content mg-x-15 text-primary">
        <h5 class="h5 mg-b-u tx-weight-bold">{{title}}</h5>
      </div>
    </template>
    <template v-slot:modal-footer>
      <section></section>
      <section>
        <button type="button" class="col-blue bg-col-white border-blue tx-weight-bold border-rounded-20 mn-h-46 f-w-180 tx-tf-upper" @click="close">{{okText}}</button>
      </section>
    </template>
    <template v-slot:modal-body>

      <section>
        <p class="tx-weight-bold">{{message}}</p>
        <div class="grid-listing staf-list-content">
          <div class="table-container pd-b-u border-rounded-wrapper">
            <b-table
              show-empty
              small
              responsive
              hover
              stacked="md"
              class="custom-table-grid"
              tfoot-class="tx-col-white border-radius-b-20"
              thead-tr-class="border-x-edge-none"
              tbody-tr-class="border-xy-edge-none border-b-primary"
              :items="slicedDocuments"
              :fields="fields"
            >
              <!-- HEADER -->
              <template v-slot:thead-top>
                <b-tr><b-td :colspan="fields.length" class="bg-col-blue f-h-42 border-t-none border-radius-t-20"></b-td></b-tr>
              </template>
              <template v-slot:head()="scope">
                <div v-if="Object.prototype.hasOwnProperty.call(titlesMap, scope.column)"><span class="tx-size-m tx-weight-bold">{{ titlesMap[scope.column] }}</span></div>
                <div v-else><span class="tx-size-m tx-weight-bold">{{ scope.label }}</span></div>
              </template>

              <!-- BODY -->
              <template v-if="hideColumn.indexOf('document_name') === -1" v-slot:cell(document_name)="row">
                <section class="mx-w-300 tx-truncate"><span class="tx-weight-bold">{{row.item.document_name}}</span></section>
              </template>
              <template v-if="hideColumn.indexOf('status') === -1" v-slot:cell(status)="row">
                <BaseStatusChip class="border" :status="{ 'key': row.item.status, 'name': _startCase(_lowerCase(row.item.status)) }"/>
              </template>
              <template v-if="hideColumn.indexOf('actions') === -1" v-slot:cell(actions)="row">
                <slot name="actions" :item="row.item">
                  <div class="tw-flex align-items-center justify-content-center">
                    <button
                      type="button"
                      class="btn-secondary button-transparent edit pd-0 ml-2 mr-2"
                    >
                      <ExternalLinkIcon 
                        size="1.5x" 
                        @click="tryToEdit(row.item)"/>
                    </button>
                  </div>
                </slot>
              </template>

              <!-- FOOTER -->
              <template v-slot:custom-foot>
                <b-tr>
                  <b-td :colspan="fields.length" class="f-h-42 bg-col-blue border-b-none border-radius-b-20">
                    <section class="fl-left">
                      <div class="fl-al-center">
                        <AlertCircleIcon size="18"/></div>
                      <div class="fl-columns">
                        <span class="pd-l-15 tx-style-italics tx-size-s tx-weight-300">{{infoMessage}}</span>
                        <span v-if="totalDocCount > MAX_VISIBLE_DOCUMENTS" class="pd-l-15 tx-style-italics tx-size-s tx-weight-300">{{secondaryInfoMessageParts.first}} <span class="tx-weight-bold">{{secondaryInfoMessageParts.second}}</span> {{secondaryInfoMessageParts.third }}</span>
                      </div>
                    </section>
                  </b-td>
                </b-tr>
              </template>
            </b-table>
          </div>
        </div>
      </section>
    </template>
  </FabricModal>
</template>
